import React from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorBoundary } from '@/components/errorBoundary';
import { useNavbarAccessory } from '@/layout';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import { VersionChip } from './components';
import { OperationEditorPageView } from './OperationEditorPageView';
import { useOperationEditorPage } from './useOperationEditorPage';

/**
 * `/accounting/operations/:key/edit` — the template editor.
 *
 * A standalone sub-page rather than a fourth tab: it is a form, not a view of
 * the catalog, and the navbar gives it a back button to the Templates tab the
 * way manual import gets one back to Purchases.
 *
 * The navbar also gets this page's **version chip** — `v4 draft`. The route
 * supplies the title, but which version the title refers to is page state, and
 * "Operation template" alone stops being a complete answer the moment an
 * unpublished draft is on screen. See `layout/navbarSlot`.
 *
 * Owns its data hook and an `ErrorBoundary`; the throw lives in the inner
 * Content child, because React boundaries don't catch errors thrown by the
 * component that renders them.
 *
 * The boundary matters more here than on the catalog. A failed read leaves the
 * editor with no recipe — and the editor's blank state is *a new empty pair*,
 * which is indistinguishable from a template that genuinely has no lines. Save
 * that and the author would replace a working recipe with an empty one because
 * the network was down. So a failed read throws.
 */
export const OperationEditorPage: React.FC = () => {
    const page = useOperationEditorPage();
    const { t } = useTranslation('pages');

    // Memoised on the chip's own value: `useNavbarAccessory` stores the node in
    // state, so a fresh element every render would loop.
    const chip = React.useMemo(
        () => <VersionChip chip={page.versionChip} />,
        [page.versionChip],
    );
    useNavbarAccessory(chip);

    const copy = React.useMemo(
        () => ({
            errorTitle: t('operations.editor.error.title'),
            errorMessage: t('operations.editor.error.message'),
            errorRetry: t('operations.editor.error.retry'),
        }),
        [t],
    );

    return (
        <div
            className="flex w-full flex-col gap-6"
            {...getNodeTestIdProp('operation-editor-page')}
        >
            <ErrorBoundary
                data-testid="operation-editor-error-boundary"
                fallbackMessage={copy.errorMessage}
                id="operation-editor-error-boundary"
                onRetry={page.onRetry}
                resetKeys={[page.error]}
                retryLabel={copy.errorRetry}
                title={copy.errorTitle}
            >
                <OperationEditorPageContent page={page} />
            </ErrorBoundary>
        </div>
    );
};

type OperationEditorPageContentProps = {
    page: ReturnType<typeof useOperationEditorPage>;
};

const OperationEditorPageContent: React.FC<OperationEditorPageContentProps> = ({
    page,
}) => {
    if (page.error) {
        throw page.error;
    }

    return (
        <OperationEditorPageView
            access={page.access}
            accountLabels={page.accountLabels}
            accounts={page.accounts}
            canRead={page.canRead}
            chartAccounts={page.chartAccounts}
            canWrite={page.canWrite}
            effectiveFrom={page.effectiveFrom}
            formError={page.formError}
            isBlocking={page.isBlocking}
            isCreating={page.isCreating}
            isDirty={page.isDirty}
            isLoading={page.isLoading}
            isPublishing={page.isPublishing}
            isSaving={page.isSaving}
            locale={page.locale}
            mode={page.mode}
            nameEn={page.nameEn}
            nameKa={page.nameKa}
            nextVersion={page.nextVersion}
            onAddPair={page.onAddPair}
            onCancelDropPair={page.onCancelDropPair}
            onConfirmDropPair={page.onConfirmDropPair}
            onCreate={page.onCreate}
            onEffectiveFromChange={page.onEffectiveFromChange}
            onLineChange={page.onLineChange}
            onLineKindChange={page.onLineKindChange}
            onNameEnChange={page.onNameEnChange}
            categories={page.categories}
            categoryId={page.categoryId}
            onCategoryChange={page.onCategoryChange}
            onNameKaChange={page.onNameKaChange}
            historyKey={page.historyKey}
            onCloseHistory={page.onCloseHistory}
            onOpenHistory={page.onOpenHistory}
            onOpenLine={page.onOpenLine}
            onPairRoleChange={page.onPairRoleChange}
            onPublish={page.onPublish}
            onRequestDropPair={page.onRequestDropPair}
            onRolesChange={page.onRolesChange}
            onSampleChange={page.onSampleChange}
            onSaveDraft={page.onSaveDraft}
            onSetBlocked={page.onSetBlocked}
            openLine={page.openLine}
            pairs={page.pairs}
            pendingDrop={page.pendingDrop}
            preview={page.preview}
            publishedPairCount={page.publishedPairCount}
            roles={page.roles}
            sampleGel={page.sampleGel}
            subtitle={page.subtitle}
            template={page.template}
            vatRateBps={page.vatRateBps}
        />
    );
};
